import React, { useEffect, useState } from 'react';
import { Dialog } from '@mui/material';
import { DateField } from '../dropdown';
import { SidePanel } from '../panel/SidePanel';
import { FormField } from '../panel/FormField';
import type { ReconciliationVariance, DeferSuggestion } from '@techgitter/contracts';
import { fetchDeferSuggestion, resolveVariance } from '../../services/reconciliationApi';
import './reconciliationPanel.css';

interface Props {
  open: boolean;
  variance: ReconciliationVariance | null;
  /** 'create' (default) suggests a fresh date; 'edit' pre-fills the deferral's
   *  existing action date + notes for rescheduling. */
  mode?: 'create' | 'edit';
  onClose: () => void;
  /** Called after a successful defer/update so the parent can refresh + toast. */
  onDeferred: () => void;
}

/** Default resolution note, tailored to the variance kind. */
function defaultNotesFor(v: ReconciliationVariance | null): string {
  return v?.varianceType === 'term'
    ? 'Deferred pending monthly→annual vendor conversion.'
    : 'Deferred pending action.';
}

/**
 * Defer a term or coverage break. Resolves it with resolutionType 'defer' + an
 * action date; the orchestrator keeps the break suppressed until that date, then
 * reopens it if it isn't actioned. For a term break the suggested date is the
 * customer's annual invoice next-run + a buffer (from the defer-suggestion
 * endpoint); the operator can adjust it.
 */
export const DeferDialog: React.FC<Props> = ({
  open,
  variance,
  mode = 'create',
  onClose,
  onDeferred,
}) => {
  const [suggestion, setSuggestion] = useState<DeferSuggestion | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionDate, setActionDate] = useState('');
  const [notes, setNotes] = useState(() => defaultNotesFor(variance));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !variance) return;
    const controller = new AbortController();
    const isEdit = mode === 'edit';
    setLoading(true);
    setError(null);
    setSuggestion(null);
    // Edit pre-fills the deferral's current values; create starts blank and
    // fills the date from the suggestion when it arrives.
    setActionDate(isEdit ? (variance.actionDate ?? '') : '');
    setNotes(isEdit ? (variance.resolutionNotes ?? defaultNotesFor(variance)) : defaultNotesFor(variance));
    setSaving(false);
    fetchDeferSuggestion(variance.id, controller.signal)
      .then((s) => {
        setSuggestion(s);
        // Only auto-fill the date in create mode (don't clobber an edit).
        if (!isEdit && s.suggestedActionDate) setActionDate(s.suggestedActionDate);
      })
      .catch((e) => {
        if (!controller.signal.aborted) {
          setError(e instanceof Error ? e.message : 'Failed to load suggested date');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [open, variance, mode]);

  if (!variance) return null;

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const canSubmit = !!actionDate && notes.trim().length > 0 && !saving;

  const handleSubmit = async () => {
    if (!actionDate || !notes.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await resolveVariance(variance.id, {
        resolutionType: 'defer',
        resolutionNotes: notes.trim(),
        actionDate,
      });
      setSaving(false);
      onDeferred();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to defer');
      setSaving(false);
    }
  };

  const title = mode === 'edit' ? 'Edit deferral' : 'Defer until…';
  const submitLabel = saving
    ? mode === 'edit'
      ? 'Saving…'
      : 'Deferring…'
    : mode === 'edit'
      ? 'Save'
      : 'Defer';
  const subtitle = `${variance.customerName ?? variance.customerId ?? 'Customer'} · ${
    variance.vendorProductName ?? variance.vendorProductSku ?? ''
  }`;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      aria-label={title}
      PaperProps={{
        sx: { background: 'transparent', boxShadow: 'none', borderRadius: 0, overflow: 'visible' },
      }}
    >
      <SidePanel
        title={title}
        submitLabel={submitLabel}
        isValid={canSubmit}
        onClose={handleClose}
        onSubmit={handleSubmit}
      >
        {error && <div className="side-panel__alert side-panel__alert--error">{error}</div>}

        <p className="recon-dialog__hint">{subtitle}</p>

        {loading && <p className="recon-dialog__hint">Loading suggested date…</p>}
        {suggestion && (
          <p className="recon-dialog__hint">
            {suggestion.annualInvoiceNextRun
              ? `Annual invoice next-run ${suggestion.annualInvoiceNextRun} → suggested ${suggestion.suggestedActionDate} (+${suggestion.bufferDays}d).`
              : 'No future annual invoice schedule found — pick the action date manually.'}
            {suggestion.vendorRenewalDate ? ` Vendor renewal: ${suggestion.vendorRenewalDate}.` : ''}
            {variance.varianceType === 'term' && !suggestion.isTermConversionCandidate
              ? ' Note: not a monthly-vendor term break.'
              : ''}
          </p>
        )}

        <DateField
          label="Action date *"
          value={actionDate || null}
          onChange={(iso) => setActionDate(iso ?? '')}
          helperText={
            variance.varianceType === 'term'
              ? 'The break stays deferred until this date, then reopens if still unresolved. Term conversions surface on the daily email as they come due.'
              : 'The break stays deferred until this date, then reopens if still unresolved.'
          }
        />

        <FormField label="Notes *">
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Why it's deferred and the planned action…"
          />
        </FormField>
      </SidePanel>
    </Dialog>
  );
};

export default DeferDialog;
